import Button from "@/components/Button";
import { FormInput, FormTextarea } from "@/components/FormField";
import DiscordIcon from "@/components/svgs/DiscordIcon";
import { DISCORD_URL } from "@/config/social";
import { useTranslation } from "@repo/shared/i18n";
import { useState } from "react";
import { useSelector } from "react-redux";

import * as api from "../../api/privateApi";
import { getUserSelector } from "../../redux/user/userSlice";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Contact = () => {
  const { t } = useTranslation(["company", "common"]);
  const { user, status } = useSelector(getUserSelector);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [touched, setTouched] = useState({ email: false, message: false });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [sendFailed, setSendFailed] = useState(false);

  const needsEmail = status !== "pending" && !user.email;

  const emailError = !email.trim()
    ? t("contact.form.errors.required")
    : !EMAIL_REGEX.test(email.trim())
      ? t("contact.form.errors.invalidEmail")
      : undefined;
  const messageError = message.trim()
    ? undefined
    : t("contact.form.errors.required");
  const isFormValid = (!needsEmail || !emailError) && !messageError;

  const setFieldTouched = (field: "email" | "message", value: boolean) => {
    setTouched((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    setTouched({ email: true, message: true });
    if (!isFormValid) {
      return;
    }

    setIsSubmitting(true);
    setSendFailed(false);
    try {
      await api.sendMessage({
        email: needsEmail ? email.trim() : user.email,
        message: message.trim(),
      });
      setIsSubmitted(true);
    } catch (e) {
      setSendFailed(true);
      console.error(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen mx-auto bg-panel-base flex justify-center h-full">
      <div className="max-w-[680px] w-full mt-12 flex flex-col gap-y-10">
        <article className="flex flex-col gap-y-2.5 px-6 mobile:px-10">
          <h1 className="text-lg mobile:text-3xl font-bold">
            {t("contact.title")}
          </h1>
          <h3 className="text-dark-secondary text-sm mobile:text-base">
            {t("contact.intro")}
          </h3>
          <a
            href={DISCORD_URL}
            target="_blank"
            rel="noreferrer"
            className="mt-2 w-fit inline-flex items-center gap-x-2 h-9 px-3 rounded text-sm font-medium text-white bg-[#5865F2] desktop:hover:bg-[#4752C4] shadow-soft-dark-bottom"
          >
            <DiscordIcon className="w-5 h-5" />
            {t("contact.discordCta")}
          </a>
        </article>

        <article className="px-6 mobile:px-10 pb-20">
          <div className="bg-inner-box border border-panel-foreground rounded-2xl p-4 mobile:p-6 flex flex-col gap-y-6 shadow-alert">
            {isSubmitted ? (
              <div className="flex flex-col gap-y-3 items-center text-center">
                <h3 className="text-base mobile:text-xl font-bold">
                  {t("contact.sent.title")}
                </h3>
                <p className="text-sm mobile:text-base text-dark-secondary">
                  {t("contact.sent.body")}
                </p>
              </div>
            ) : (
              <>
                <h3 className="text-base mobile:text-xl font-bold">
                  {t("contact.form.title")}
                </h3>
                {needsEmail && (
                  <FormInput
                    label={t("common:email")}
                    placeholder={t("contact.form.emailPlaceholder")}
                    value={email}
                    onChange={setEmail}
                    onBlur={() => setFieldTouched("email", true)}
                    onFocus={() => setFieldTouched("email", false)}
                    type="email"
                    error={touched.email ? emailError : undefined}
                  />
                )}
                <FormTextarea
                  label={t("contact.form.messageLabel")}
                  placeholder={t("contact.form.messagePlaceholder")}
                  value={message}
                  onChange={setMessage}
                  onBlur={() => setFieldTouched("message", true)}
                  onFocus={() => setFieldTouched("message", false)}
                  maxLength={500}
                  error={touched.message ? messageError : undefined}
                />
                <div className="flex flex-col gap-y-3 items-center">
                  <div className="w-[166px]">
                    <Button
                      variant="primary"
                      label={
                        isSubmitting
                          ? t("contact.form.sending")
                          : t("contact.form.send")
                      }
                      onClick={handleSubmit}
                      large
                      disabled={isSubmitting}
                    />
                  </div>
                  {sendFailed && (
                    <p className="text-sm text-dark-disabled text-center">
                      {t("contact.form.sendFailed")}
                    </p>
                  )}
                </div>
                <p className="text-sm text-dark-tertiary">
                  {!needsEmail && user.email
                    ? `${t("contact.form.replyTo", { email: user.email })} ${t("contact.form.note")}`
                    : t("contact.form.note")}
                </p>
              </>
            )}
          </div>
        </article>
      </div>
    </main>
  );
};

export default Contact;
